import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ExtendedAccount } from "@common/types/reports/types";
import { StreamableFile } from "@nestjs/common";
import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    accountMergeBlockerSchema,
    bulkAccountOperationBodySchema,
    bulkAccountOperationResponseSchema,
    bulkUpdateAccountsBodySchema,
    createAccountBodySchema,
    getCoaLastUpdatedByEntityIdsQuerySchema,
    mergeAccountsBodySchema,
    mergeAccountsPreviewQuerySchema,
    mergeAccountsPreviewResponseSchema,
    reassignAccountRoleBodySchema,
    updateAccountBodySchema,
} from "../schemas/domains/accounting/account.schema";
import { EntityAccountGroupSchema } from "@common/schemas/domains/accounting/account-group.schema";
import { chartOfAccountsExportQuery } from "../schemas/domains/accounting/report.schema";
import { Account } from "../types/domains/accounting/account";
import {
    AccountSubtype,
    AccountType,
} from "../constants/domains/accounting/accounts";

const c = initContract();

export interface CreateAccountParams {
    account: {
        name: string;
        type: AccountType;
        subtype: AccountSubtype;
    };
}

export const accountContract = c.router(
    {
        getAccountsForWorkspace: {
            method: "GET",
            path: "/workspace/:workspaceId",
            responses: {
                200: c.type<{
                    accounts: Array<{
                        entityId: number;
                        accounts: Account[];
                        groups: EntityAccountGroupSchema[];
                    }>;
                }>(),
            },
            query: z.object({
                ledgerBasis: z.nativeEnum(AccountingLedgerBasis).optional(),
            }),
        },
        getCoaLastUpdated: {
            method: "GET",
            path: "/coa-last-updated",
            query: getCoaLastUpdatedByEntityIdsQuerySchema,
            responses: {
                200: c.type<{
                    coaLastUpdatedByEntityId: Record<number, string | null>;
                }>(),
                400: ErrorMessageSchema,
            },
        },
        getAccountsForEntity: {
            method: "GET",
            path: "/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: c.type<{
                    accounts: Account[];
                }>(),
            },
        },
        createAccount: {
            method: "POST",
            path: "/create/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ accounts: ExtendedAccount[] }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
            body: createAccountBodySchema,
        },
        disableAccountsBulk: {
            method: "POST",
            path: "/bulk-disable",
            responses: {
                200: bulkAccountOperationResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: bulkAccountOperationBodySchema,
        },
        enableAccountsBulk: {
            method: "POST",
            path: "/bulk-enable",
            responses: {
                200: bulkAccountOperationResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: bulkAccountOperationBodySchema,
        },
        deleteAccountsBulk: {
            method: "POST",
            path: "/bulk-delete",
            responses: {
                200: bulkAccountOperationResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: bulkAccountOperationBodySchema,
        },
        mergeAccountsPreview: {
            method: "GET",
            path: "/:entityId/merge-preview",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            query: mergeAccountsPreviewQuerySchema,
            responses: {
                200: mergeAccountsPreviewResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        mergeAccounts: {
            method: "POST",
            path: "/:entityId/merge",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: z.object({ message: z.string() }),
                400: z.object({
                    message: z.string(),
                    blockers: z.array(accountMergeBlockerSchema).default([]),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: mergeAccountsBodySchema,
        },
        reassignAccountRole: {
            method: "POST",
            path: "/:entityId/reassign-role",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: c.type<{ accounts: Account[] }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: reassignAccountRoleBodySchema,
        },
        bulkUpdateAccounts: {
            method: "PATCH",
            path: "/bulk-update",
            responses: {
                200: c.type<{ accounts: Account[] }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: bulkUpdateAccountsBodySchema,
        },
        updateAccount: {
            method: "PATCH",
            path: "/:entityId/:id",
            pathParams: z.object({
                entityId: z.coerce.number(),
                id: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ account: Account }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
            body: updateAccountBodySchema,
        },
        exportChartOfAccountsCsv: {
            method: "GET",
            path: "/chart-of-accounts/csv",
            query: chartOfAccountsExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportChartOfAccountsXlsx: {
            method: "GET",
            path: "/chart-of-accounts/xlsx",
            query: chartOfAccountsExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/accounting/accounts",
    },
);
